/** 용지 크기(mm, 세로 기준) */
export const PAPER_MM = { A4: [210, 297], A3: [297, 420], B5: [182, 257], Letter: [216, 279] };
export function printMargins(m) {
    const d = { top: 14, right: 10, bottom: 14, left: 10 };
    if (typeof m === 'number')
        return { top: m, right: m, bottom: m, left: m };
    return { ...d, ...(m ?? {}) };
}
const num = (v, d) => { const n = Number(v); return Number.isFinite(n) ? n : d; };
const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
export function buildPrintHtml(grid, o = {}) {
    const cols = grid.printColumns(o.columns);
    // printRows 는 화면 컬럼 전부를 돌려주므로 고른 컬럼(순서 포함)으로 다시 맞춤
    const all = grid.printColumns(), pick = cols.map(c => all.findIndex(a => a.id === c.id));
    const rows = grid.printRows(o.maxRows ?? 5000).map(r => (r.kind === 'row' ? { kind: 'row', cells: pick.map(i => r.cells[i]) } : r));
    // 옵션은 앱·저장 값에서 올 수 있으므로 정해진 값·숫자만 (HTML·CSS 에 끼워 넣지 못하게)
    o = { ...o, paper: (o.paper && o.paper in PAPER_MM ? o.paper : 'A4'), orientation: o.orientation === 'landscape' ? 'landscape' : 'portrait',
        fontSize: num(o.fontSize, 8.5), scale: num(o.scale, 100), maxRows: num(o.maxRows, 5000), margin: typeof o.margin === 'number' ? num(o.margin, 12) : o.margin && { top: num(o.margin.top, 14), right: num(o.margin.right, 10), bottom: num(o.margin.bottom, 14), left: num(o.margin.left, 10) } };
    const mg = printMargins(o.margin), sc = (o.scale ?? 100) / 100;
    const [pw0, ph0] = PAPER_MM[o.paper ?? 'A4'] ?? PAPER_MM.A4;
    const pageW = (o.orientation === 'landscape' ? ph0 : pw0) - mg.left - mg.right;
    const total = grid.getViewRows().length;
    const t = grid.t ?? ((k, v) => (v ? k.replace(/\{(\w+)\}/g, (m, x) => (x in v ? String(v[x]) : m)) : k));
    const lang = grid.opts?.locale === 'en' ? 'en' : 'ko';
    const now = new Date();
    const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const totalW = cols.reduce((a, c) => a + c.width, 0) + (o.showRowNumber ? 40 : 0);
    const fit = o.fitWidth !== false;
    const px2mm = 25.4 / 96;
    const colgroup = fit
        ? (o.showRowNumber ? `<col style="width:${(40 / totalW) * 100}%">` : '') + cols.map(c => `<col style="width:${((c.width / totalW) * 100).toFixed(2)}%">`).join('')
        : (o.showRowNumber ? `<col style="width:${(40 * px2mm).toFixed(1)}mm">` : '') + cols.map(c => `<col style="width:${(c.width * px2mm).toFixed(1)}mm">`).join('');
    const head = `<tr>${o.showRowNumber ? '<th>No</th>' : ''}${cols.map(c => `<th>${esc(c.header)}</th>`).join('')}</tr>`;
    let no = 0;
    const rowHtml = (r) => {
        if (r.kind === 'group')
            return `<tr class="grp"><td colspan="${cols.length + (o.showRowNumber ? 1 : 0)}">${'\u3000'.repeat(r.level ?? 0)}${esc(r.label)}</td></tr>`;
        no++;
        return `<tr>${o.showRowNumber ? `<td class="c">${no}</td>` : ''}${r.cells.map((t, i) => `<td class="${cols[i].align}">${esc(t)}</td>`).join('')}</tr>`;
    };
    // 그룹별 쪽 나눔·그룹 머리행 반복: 최상위 그룹마다 표 하나 (thead = 머리글 + 그룹 머리행 → 브라우저·PDF 모두 쪽마다 반복)
    const perGroup = (o.pageBreakPerGroup || o.repeatGroupHeader) && rows.some(r => r.kind === 'group' && !r.level);
    const segs = [];
    if (perGroup) {
        for (const r of rows) {
            if (r.kind === 'group' && !r.level)
                segs.push({ head: rowHtml(r), body: '' });
            else {
                if (!segs.length)
                    segs.push({ head: '', body: '' });
                segs[segs.length - 1].body += rowHtml(r);
            }
        }
    }
    else
        segs.push({ head: '', body: rows.map(rowHtml).join('') });
    const foot = grid.opts.showFooter ? `<tfoot><tr>${o.showRowNumber ? '<td></td>' : ''}${grid.printFooter(cols.map(c => c.id)).map((t, i) => `<td class="${i === 0 ? 'l' : cols[i].align}">${esc(t)}</td>`).join('')}</tr></tfoot>` : '';
    const approvals = o.approvals?.length ? `<table class="appr"><tr>${o.approvals.map(a => `<th>${esc(a)}</th>`).join('')}</tr><tr>${o.approvals.map(() => '<td></td>').join('')}</tr></table>` : '';
    const truncated = total > rows.filter(r => r.kind === 'row').length ? `<p class="warn">${esc(t('전체 {total}건 중 {max}건만 인쇄했습니다. 필터로 범위를 줄여 주세요.', { total: total.toLocaleString('ko-KR'), max: o.maxRows ?? 5000 }))}</p>` : '';
    return `<!doctype html><html lang="${lang}"><head><meta charset="utf-8"><title>${esc(o.title ?? t('인쇄'))}</title><style>
@page { size: ${o.paper ?? 'A4'} ${o.orientation ?? 'portrait'}; margin: ${mg.top}mm ${mg.right}mm ${mg.bottom}mm ${mg.left}mm;
  ${o.headerText ? `@top-center { content: "${esc(o.headerText)}"; font: 8pt "Malgun Gothic", sans-serif; color: #555 }` : ''}
  @bottom-center { content: ${o.footerText ? `"${esc(o.footerText)}  ·  "` : ''}counter(page) " / " counter(pages); font: 8pt "Malgun Gothic", sans-serif; color: #555 }
  @bottom-left { content: "${esc(o.footerLeft ?? '')}"; font: 8pt "Malgun Gothic", sans-serif; color: #555 }
  @bottom-right { content: "${esc(t('출력 {stamp}', { stamp }))}"; font: 8pt "Malgun Gothic", sans-serif; color: #555 } }
* { box-sizing: border-box }
html { --page-w: ${pageW}mm }
body { margin: 0; ${sc !== 1 ? `zoom: ${sc};` : ''} font: ${o.fontSize ?? 8.5}pt/1.35 "Malgun Gothic", "Apple SD Gothic Neo", sans-serif; color: #000; -webkit-print-color-adjust: exact; print-color-adjust: exact }
.top { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 4mm; gap: 6mm }
.top .meta { font-size: 8pt; color: #333 }
h1 { font-size: 15pt; margin: 0 0 1mm }
.sub { font-size: 9pt; color: #333 }
table.g { width: ${fit ? '100%' : 'auto'}; border-collapse: collapse; table-layout: fixed }
table.g th, table.g td { border: 0.5pt solid #777; padding: 1.2mm 1.4mm; overflow: hidden; white-space: nowrap; text-overflow: ellipsis }
table.g thead th { background: #e9eef3; font-weight: 700 }
table.g thead { display: table-header-group }
table.g tfoot { display: table-row-group; font-weight: 700; background: #f3f5f7 }
table.g tr { break-inside: avoid }
table.g tr.grp td { background: #f0f3f6; font-weight: 700 }
table.g.brk { break-before: page }
table.g + table.g { margin-top: 3mm }
.r { text-align: right } .c { text-align: center } .l { text-align: left }
table.appr { border-collapse: collapse } table.appr th, table.appr td { border: 0.5pt solid #555; width: 18mm; text-align: center; font-size: 8pt; padding: 1mm } table.appr td { height: 12mm }
.warn { color: #b00; font-size: 8pt }
${o.watermark ? `.wm { position: fixed; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 60pt; color: rgba(0,0,0,.06); transform: rotate(-30deg); pointer-events: none }` : ''}
</style></head><body>
${o.watermark ? `<div class="wm">${esc(o.watermark)}</div>` : ''}
<div class="top"><div><div class="meta">${esc(o.headerLeft ?? '')}</div><h1>${esc(o.title ?? '')}</h1>${o.subtitle ? `<div class="sub">${esc(o.subtitle)}</div>` : ''}<div class="meta">${esc(o.headerRight ?? '')} · ${esc(t('{n}건', { n: total.toLocaleString('ko-KR') }))}</div></div>${approvals}</div>
${truncated}
${o.headerText ? `<div class="sub hdr-text">${esc(o.headerText)}</div>` : ''}
${segs.map((sg, i) => `<table class="g${o.pageBreakPerGroup && i ? ' brk' : ''}"${o.pageBreakPerGroup && i ? ' data-break="1"' : ''} data-fit="${fit}"><colgroup>${colgroup}</colgroup><thead>${head}${sg.head}</thead><tbody>${sg.body}</tbody>${i === segs.length - 1 ? foot : ''}</table>`).join('\n')}
${o.footerText ? `<div class="sub ftr-text">${esc(o.footerText)}</div>` : ''}
</body></html>`;
}
/** HTML 문서를 숨은 iframe 에 넣고 인쇄 대화상자를 연다 */
export function printHtml(html) {
    return new Promise(resolve => {
        const f = document.createElement('iframe');
        f.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
        document.body.appendChild(f);
        const doc = f.contentDocument;
        doc.open();
        doc.write(html);
        doc.close();
        const go = () => { f.contentWindow.focus(); f.contentWindow.print(); setTimeout(() => { f.remove(); resolve(); }, 500); };
        if (doc.readyState === 'complete')
            setTimeout(go, 50);
        else
            f.onload = go;
    });
}
export function printGrid(grid, o = {}) { return printHtml(buildPrintHtml(grid, o)); }
/** 서식 템플릿 치환. 값은 기본 HTML 이스케이프 */
export function renderTemplate(tpl, data) {
    const section = /\{\{([#^])(\w+)\}\}([\s\S]*?)\{\{\/\2\}\}/g;
    const fill = (t, ctx) => t.replace(section, (_, kind, key, inner) => {
        const v = ctx[key];
        const list = Array.isArray(v) ? v : v ? [v] : [];
        if (kind === '^')
            return list.length ? '' : fill(inner, ctx);
        return list.map((item, i) => fill(inner, { ...ctx, ...(typeof item === 'object' ? item : { '.': item }), '@index': i, '@no': i + 1 })).join('');
    })
        .replace(/\{\{\{\s*([\w@.가-힣]+)\s*\}\}\}/g, (_, k) => String(ctx[k] ?? ''))
        .replace(/\{\{\s*([\w@.가-힣]+)\s*\}\}/g, (_, k) => esc(ctx[k]));
    return fill(tpl, data);
}
